import { useState, useEffect, useCallback, useRef } from 'react';

export const usePusherConnection = (user) => {
    const [isConnected, setIsConnected] = useState(false);
    const checkConnectionRef = useRef(null);
    
    // Check connection status
    useEffect(() => {
        if (checkConnectionRef.current) {
            clearInterval(checkConnectionRef.current);
        }
        
        checkConnectionRef.current = setInterval(() => {
            if (window.Echo?.connector?.pusher?.connection?.state === 'connected') {
                setIsConnected(true);
                clearInterval(checkConnectionRef.current);
            }
        }, 100);
        
        return () => {
            if (checkConnectionRef.current) {
                clearInterval(checkConnectionRef.current);
            }
        };
    }, []);
    
    // Memoize subscribeToChannel to prevent unnecessary recreations
    const subscribeToChannel = useCallback((channelName, eventTypes, handler) => {
        if (!isConnected || !window.Echo || !user?.id) return null;
        
        try {
            const channel = window.Echo.private(channelName);
            
            eventTypes.forEach(eventType => {
                console.log(`Setting up listener for event: ${eventType} on channel: ${channelName}`);
                
                channel.listen(eventType, (data) => {
                    console.log(`🔔 Received event: ${eventType} on channel: ${channelName}`, data);
                    
                    // Check if user is a parent by looking at roles
                    const isParent = user.roles?.some(role => role.name === "parent");
                    
                    // For message notifications, parents should receive all messages for their selected child
                    // For other types of notifications, we still need to filter
                    if (isParent && eventType !== 'SentMessage') {
                        const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
                        const isRelevantNotification = 
                            data.child_id === selectedChildId ||
                            data.recipient_id === selectedChildId ||
                            data.to_id === selectedChildId;
                        
                        console.log(`Parent user filter - relevant: ${isRelevantNotification}`, {
                            selectedChildId,
                            data
                        });
                        
                        if (isRelevantNotification) {
                            handler(data, eventType);
                        }
                    } else {
                        // For message notifications or non-parent users, handle directly
                        console.log(`Handling event directly for user: ${user.id}`);
                        handler(data, eventType);
                    }
                });
                
                // Add error handling for the channel
                channel.error((error) => {
                    console.error(`❌ Channel error on ${channelName}:`, error);
                });
            });
            
            return () => {
                eventTypes.forEach(eventType => {
                    try {
                        channel.stopListening(eventType);
                    } catch (error) {
                        console.error(`Error cleaning up event ${eventType}:`, error);
                    }
                });
            };
        } catch (error) {
            console.error(`Error subscribing to channel ${channelName}:`, error);
            return null;
        }
    }, [isConnected, user?.id]); // Only depend on connection status and user ID
    
    // Memoize notification subscriptions
    const subscribeToPalNotifications = useCallback((handler) => {
        if (!user?.id) return null;
        
        // Determine appropriate channel based on user role
        let channelName;
        if (user.name === "parent") {
            channelName = `notifications.${user.id}`;
        } else if (user.roles?.some(role => role.name === "mentor")) {
            // Use a mentor-specific channel or the notifications channel
            channelName = `notifications.${user.id}`;
        } else {
            channelName = `friend-requests.${user.id}`;
        }
        
        console.log(`Subscribing to channel: ${channelName}`);
        
        return subscribeToChannel(
            channelName,
            ['notification', 'SentNotification'],
            handler
        );
    }, [subscribeToChannel, user]);
    
    const subscribeToMenuNotifications = useCallback((handler) => {
        if (!user?.id || user?.name !== "parent") return null;
        
        return subscribeToChannel(
            `notifications.${user.id}`,
            ['notification'],
            handler
        );
    }, [subscribeToChannel, user?.id, user?.name]);
    
    // New method to subscribe to message notifications
    const subscribeToMessageNotifications = useCallback((handler) => {
        if (!user?.id) return null;
        
        // Check if user is a parent by looking at roles
        const isParent = user.roles?.some(role => role.name === "parent");
        
        // For parents, listen to their children's messages
        if (isParent) {
            const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
            if (selectedChildId) {
                console.log(`Parent subscribing to messages for child: ${selectedChildId}`);
                return subscribeToChannel(
                    `messages.${selectedChildId}`,
                    ['SentMessage'],
                    handler
                );
            }
        } else {
            // For children and mentors, listen to their own messages
            console.log(`User subscribing to messages: ${user.id}`);
            return subscribeToChannel(
                `messages.${user.id}`,
                ['SentMessage'],
                handler
            );
        }
        
        return null;
    }, [subscribeToChannel, user]);
    
    return {
        isConnected,
        subscribeToPalNotifications,
        subscribeToMenuNotifications,
        subscribeToMessageNotifications
    };
};